import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FaSpinner,
  FaMagic,
  FaMapMarkerAlt,
  FaEye,
  FaDirections,
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle,
  FaCompass,
  FaStar,
  FaGoogle,
  FaYelp,
  FaTripadvisor,
  FaExternalLinkAlt,
  FaTimes,
  FaLandmark,
  FaTree,
  FaUtensils,
  FaStore,
  FaMonument,
} from 'react-icons/fa';
import { getPublicPreview, trackEvent } from '../../api/client';
import { getPendingTripContext } from '../../utils/tripContext';
import BookingBlock from '../BookingBlock';
import SavePlanByEmail from '../SavePlanByEmail';
import TripCostEstimator from '../tools/TripCostEstimator';

/** Destination → sub-ID fragment. The backend strips anything else anyway. */
const slugify = (value) =>
  (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 32);

const PREVIEW_API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const previewBuildDirectionsUrl = (attractions) => {
  const valid = (attractions || []).filter((a) => a.lat != null && a.lng != null);
  if (valid.length === 0) return null;
  if (valid.length === 1) {
    return `https://www.google.com/maps/dir/?api=1&destination=${valid[0].lat},${valid[0].lng}`;
  }
  const origin = `${valid[0].lat},${valid[0].lng}`;
  const dest = `${valid[valid.length - 1].lat},${valid[valid.length - 1].lng}`;
  const waypoints = valid.slice(1, -1).map((l) => `${l.lat},${l.lng}`).join('|');
  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`;
  if (waypoints) url += `&waypoints=${encodeURIComponent(waypoints)}`;
  return url;
};

const previewAttractionDirectionsUrl = (a, destination) => {
  if (a.lat != null && a.lng != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${a.lat},${a.lng}`;
  }
  const q = destination ? `${a.name} ${destination}` : a.name;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`;
};

const previewImageCache = new Map();

const usePreviewPlaceImage = (name, lat, lng) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!name) return;
    const key = `${name}|${lat}|${lng}`;
    if (previewImageCache.has(key)) {
      setUrl(previewImageCache.get(key));
      return;
    }
    let cancelled = false;
    let q = `name=${encodeURIComponent(name)}`;
    if (lat != null) q += `&lat=${encodeURIComponent(lat)}`;
    if (lng != null) q += `&lng=${encodeURIComponent(lng)}`;
    fetch(`${PREVIEW_API_BASE}/place-image?${q}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const found = data && data.image_url ? `${PREVIEW_API_BASE}/place-image-proxy?${q}` : null;
        if (found) previewImageCache.set(key, found);
        setUrl(found);
      })
      .catch(() => { if (!cancelled) setUrl(null); });
    return () => { cancelled = true; };
  }, [name, lat, lng]);
  return url;
};

const PreviewStopThumb = ({ name, lat, lng }) => {
  const url = usePreviewPlaceImage(name, lat, lng);
  if (!url) {
    return (
      <div className="w-10 h-10 rounded-md flex-shrink-0 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <FaMapMarkerAlt className="text-slate-400 text-sm" />
      </div>
    );
  }
  return <img src={url} alt={name} loading="lazy" className="w-10 h-10 rounded-md object-cover flex-shrink-0" />;
};

const PreviewDayCard = ({ destination, attractions, weather, onViewDay }) => {
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const totalStops = attractions?.length || 0;
  const directionsUrl = previewBuildDirectionsUrl(attractions);

  return (
    <div className="relative bg-white rounded-xl border-2 border-amber-400 shadow-md ring-2 ring-amber-100 overflow-hidden flex flex-col">
      <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-lg text-xs font-bold shadow-sm bg-amber-500 text-white">
              Day 1
            </span>
            <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
              Today
            </span>
          </div>
          {weather && (
            <div
              className="flex items-center gap-1.5 text-sm bg-white/80 px-2 py-1 rounded-lg shadow-sm"
              title={weather.description || ''}
            >
              <span className="text-lg">{weather.emoji}</span>
              {weather.temp_max_f != null && (
                <span className="font-semibold text-slate-700">{Math.round(weather.temp_max_f)}°</span>
              )}
            </div>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2 font-medium">{formattedDate}</p>
        <h3 className="font-bold text-slate-800 mt-0.5 truncate text-lg" title={destination}>
          {destination}
        </h3>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="space-y-2.5 mb-4 flex-1">
          {totalStops > 0 ? (
            <>
              {attractions.slice(0, 3).map((loc, idx) => (
                <div key={`${loc.name}-${idx}`} className="flex items-start gap-3 text-sm">
                  <PreviewStopThumb name={loc.name} lat={loc.lat} lng={loc.lng} />
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-sm flex-shrink-0 mt-0.5 bg-indigo-500 text-white">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="truncate font-medium text-slate-700 block" title={loc.name}>
                      {loc.name}
                    </span>
                    {loc.lat != null && loc.lng != null && (
                      <span className="text-xs text-slate-400 font-mono block">
                        {Number(loc.lat).toFixed(4)}, {Number(loc.lng).toFixed(4)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {totalStops > 3 && (
                <p className="text-xs text-indigo-500 font-medium pl-9">
                  +{totalStops - 3} more stops
                </p>
              )}
            </>
          ) : (
            <div className="text-center py-4 text-slate-400 text-sm">
              <FaMapMarkerAlt className="mx-auto mb-1 text-lg text-slate-300" />
              No stops planned
            </div>
          )}
        </div>

        {totalStops > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-slate-500 font-medium">Stops</span>
              <span className="text-emerald-600 font-bold">{totalStops}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden shadow-inner">
              <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full" style={{ width: '100%' }} />
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mt-auto">
          <button
            onClick={onViewDay}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500 text-white text-sm font-semibold rounded-lg transition-all shadow-sm hover:shadow"
          >
            <FaEye className="text-xs" />
            View Day
          </button>
          {directionsUrl && (
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center p-2.5 bg-sky-100 hover:bg-sky-200 text-sky-600 rounded-lg transition-colors"
              title="Get directions"
            >
              <FaDirections />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const STORAGE_KEY = 'is_preview_cache_v2';
// In-memory mirror of the cache. The landing page hands its generated preview to
// the Tryout page through this cache, so the handoff must survive localStorage
// being unavailable (private browsing, quota) — that only costs us persistence
// across a full page reload, not the handoff itself.
let lastPreview = null;
const TYPE_SPEED_MS = 12;

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve();
  if (document.getElementById('cf-turnstile-script')) {
    return new Promise((resolve) => {
      const i = setInterval(() => {
        if (window.turnstile) { clearInterval(i); resolve(); }
      }, 50);
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = 'cf-turnstile-script';
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
};

const useTypewriter = (text, speed = TYPE_SPEED_MS) => {
  const [shown, setShown] = useState('');
  useEffect(() => {
    if (!text) { setShown(''); return; }
    setShown('');
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) clearInterval(id);
    }, speed);
    return () => clearInterval(id);
  }, [text, speed]);
  return shown;
};

const previewMoreInfoUrl = (name, destination) => {
  const q = destination ? `${name} ${destination}` : name;
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
};

const previewViewOnMapUrl = (loc) => {
  if (loc.lat != null && loc.lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.name)}`;
};

const PreviewReviewsDropdown = ({ name, location, lat, lng }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const googleUrl = lat != null && lng != null
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`
    : `https://www.google.com/maps/search/${encodeURIComponent(location ? name + ' ' + location : name)}`;
  const yelpUrl = `https://www.yelp.com/search?find_desc=${encodeURIComponent(name)}&find_loc=${encodeURIComponent(location || '')}`;
  const tripUrl = `https://www.tripadvisor.com/Search?q=${encodeURIComponent(location ? name + ' ' + location : name)}`;

  const sources = [
    { name: 'Google', icon: FaGoogle, url: googleUrl, color: 'text-blue-600 hover:text-blue-700', bg: 'hover:bg-blue-50' },
    { name: 'Yelp', icon: FaYelp, url: yelpUrl, color: 'text-red-600 hover:text-red-700', bg: 'hover:bg-red-50' },
    { name: 'TripAdvisor', icon: FaTripadvisor, url: tripUrl, color: 'text-green-600 hover:text-green-700', bg: 'hover:bg-green-50' },
  ];

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-md transition-colors"
        title={`View reviews for ${name}`}
      >
        <FaStar className="text-amber-500" size={10} />
        Reviews
        <FaChevronDown size={9} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-neutral-200 z-20 overflow-hidden">
          {sources.map((s) => {
            const Icon = s.icon;
            return (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2 px-3 py-2 text-xs font-medium ${s.color} ${s.bg} transition-colors`}
              >
                <Icon size={12} />
                <span className="flex-1">{s.name}</span>
                <FaExternalLinkAlt size={9} className="opacity-50" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PreviewAttractionCard = ({ attraction, destination, delay = 0, nextLoc, onExplore }) => {
  const [start, setStart] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStart(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  const typed = useTypewriter(start ? attraction.description : '');
  const imageUrl = usePreviewPlaceImage(attraction.name, attraction.lat, attraction.lng);
  const hasCoords = attraction.lat != null && attraction.lng != null;

  return (
    <div className="rounded-xl border-2 border-slate-200 bg-white shadow-sm hover:border-sky-300 hover:shadow transition-all mb-3 overflow-hidden">
      <div className="flex items-start gap-3 p-4">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={attraction.name}
            loading="lazy"
            className="w-[72px] h-[72px] rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-[72px] h-[72px] rounded-lg flex-shrink-0 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
            <FaMapMarkerAlt className="text-slate-400 text-xl" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-slate-800 truncate" title={attraction.name}>
            {attraction.name}
          </h4>
          {typed && (
            <div className="mt-2">
              <p className={`text-sm text-slate-600 leading-relaxed ${!expanded ? 'line-clamp-3' : ''}`}>
                {typed}
              </p>
              {typed.length > 120 && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="text-xs text-sky-600 hover:text-sky-700 mt-1.5 flex items-center gap-1 font-medium"
                >
                  {expanded ? (
                    <>Show less <FaChevronUp size={10} /></>
                  ) : (
                    <>Show more <FaChevronDown size={10} /></>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center border-t border-slate-100 bg-slate-50/50">
        <a
          href={previewMoreInfoUrl(attraction.name, destination)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors border-r border-slate-100"
        >
          <FaInfoCircle />
          More Info
        </a>
        <a
          href={previewViewOnMapUrl(attraction)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-sky-600 hover:bg-sky-50 transition-colors border-r border-slate-100"
        >
          <FaMapMarkerAlt />
          View on Map
        </a>
        {hasCoords && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${attraction.lat},${attraction.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-teal-600 hover:bg-teal-50 transition-colors border-r border-slate-100"
          >
            <FaDirections />
            Directions
          </a>
        )}
        <div className="flex-1 flex items-center justify-center py-2.5">
          <PreviewReviewsDropdown
            name={attraction.name}
            location={destination}
            lat={attraction.lat}
            lng={attraction.lng}
          />
        </div>
      </div>

      {nextLoc && nextLoc.lat != null && nextLoc.lng != null && hasCoords && onExplore && (
        <div className="border-t border-slate-100 bg-gradient-to-r from-emerald-50 to-teal-50">
          <button
            type="button"
            onClick={() => onExplore(attraction, nextLoc)}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
          >
            <FaCompass className="text-emerald-500" />
            Explore stops along the way to {nextLoc.name.length > 25 ? nextLoc.name.substring(0, 25) + '...' : nextLoc.name}
          </button>
        </div>
      )}
    </div>
  );
};

const previewExploreCategoryIcons = {
  'Attraction': FaLandmark,
  'Historic Site': FaMonument,
  'Park/Garden': FaTree,
  'Food & Drink': FaUtensils,
  'Natural Wonder': FaTree,
  'Local Shop': FaStore,
  'Point of Interest': FaMapMarkerAlt,
};

const previewFormatKm = (km) => {
  if (km == null) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
};

const fetchPublicExplore = async (startCoords, endCoords, turnstileToken) => {
  const url = `${PREVIEW_API_BASE}/public/explore-between`;
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      start_coords: startCoords,
      end_coords: endCoords,
      turnstile_token: turnstileToken,
    }),
  });
  if (!r.ok) {
    const err = new Error(`explore_${r.status}`);
    err.status = r.status;
    throw err;
  }
  return r.json();
};

const PreviewExploreModal = ({ isOpen, onClose, fromName, toName, loading, error, suggestions, onRetry }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FaMapMarkerAlt className="text-amber-200" />
                Discover Places Between
              </h2>
              <p className="text-amber-100 text-sm mt-1">{fromName} → {toName}</p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition-colors p-1">
              <FaTimes className="text-xl" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FaSpinner className="animate-spin text-4xl text-amber-500 mb-4" />
              <p className="text-neutral-600">Discovering nearby places...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={onRetry}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : !suggestions || suggestions.length === 0 ? (
            <div className="text-center py-12">
              <FaMapMarkerAlt className="text-5xl text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-600">No suggestions found for this route segment.</p>
              <p className="text-neutral-400 text-sm mt-2">Try a different segment or check back later.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {suggestions.map((s, i) => {
                const Icon = previewExploreCategoryIcons[s.category] || FaMapMarkerAlt;
                const mapsUrl = s.lat != null && s.lng != null
                  ? `https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`
                  : `https://www.google.com/maps/search/${encodeURIComponent(s.name)}`;
                return (
                  <div key={`${s.name}-${i}`} className="border border-neutral-200 rounded-xl p-4 hover:border-amber-300 hover:shadow-md transition-all">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="text-amber-500 flex-shrink-0" />
                          <h3 className="font-semibold text-neutral-800 truncate">{s.name}</h3>
                        </div>
                        <p className="text-sm text-neutral-500 mb-2">
                          {s.category}
                          {s.subcategory && ` • ${s.subcategory}`}
                          {s.distance_from_route_km != null && (
                            <> {' • '}<span className="text-amber-600">{previewFormatKm(s.distance_from_route_km)} from route</span></>
                          )}
                        </p>
                        {s.description && <p className="text-sm text-neutral-600 mb-2">{s.description}</p>}
                        {s.opening_hours && <p className="text-xs text-neutral-400">Hours: {s.opening_hours}</p>}
                        <div className="flex items-center gap-3 mt-2">
                          <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700"
                          >
                            <FaMapMarkerAlt className="text-[10px]" /> View on Map
                          </a>
                          {s.website && (
                            <a
                              href={s.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700"
                            >
                              <FaExternalLinkAlt className="text-[10px]" /> Website
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-neutral-200 p-4 bg-neutral-50">
          <p className="text-xs font-semibold text-amber-600 text-center animate-pulse">
            Sign up free to add discovered places to your itinerary and re-optimize your route.
          </p>
        </div>
      </div>
    </div>
  );
};

// renderResults=false renders the form only — used on the landing page, where a
// successful generation hands off to the Tryout page (via onPreviewReady) and the
// preview card is rendered there from the localStorage cache.
const TryItNowWidget = ({ renderResults = true, onPreviewReady, initialDestination = '' }) => {
  const [destination, setDestination] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [showAttractions, setShowAttractions] = useState(false);
  const [explore, setExplore] = useState({
    open: false,
    loading: false,
    error: null,
    suggestions: [],
    fromName: '',
    toName: '',
    fromLoc: null,
    toLoc: null,
  });
  const attractionsRef = useRef(null);
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Set when the widget arrives with a destination carried in from another
  // surface (a blog article's "Plan this trip free") and must generate without
  // the visitor typing anything.
  const autoRun = useRef(false);

  useEffect(() => {
    // A destination handed in from an article beats any cached preview: the
    // visitor asked for that trip, not the last one they looked at. Only an
    // explicit hand-off generates on its own — a destination merely remembered
    // from an earlier visit is pre-filled and left for the visitor to submit.
    const handedOff = (initialDestination || '').trim();
    const carried = handedOff || (getPendingTripContext()?.destination || '').trim();
    if (carried) {
      setDestination(carried);
      const cached =
        lastPreview && lastPreview.destination.toLowerCase() === carried.toLowerCase()
          ? lastPreview
          : null;
      if (cached) {
        setPreview(cached.preview);
      } else if (handedOff) {
        autoRun.current = true;
      }
      return;
    }
    if (lastPreview) {
      setDestination(lastPreview.destination);
      setPreview(lastPreview.preview);
      return;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const { destination: d, preview: p } = JSON.parse(raw);
        if (d && p) {
          setDestination(d);
          setPreview(p);
        }
      }
    } catch {
      // ignore corrupted cache
    }
  }, [initialDestination]);

  const turnstileCallbacksRef = useRef({ resolve: null, reject: null });

  const ensureTurnstile = useCallback(async () => {
    if (!TURNSTILE_SITE_KEY) return 'dev';
    await loadTurnstile();
    return new Promise((resolve, reject) => {
      // Stash callbacks so the widget's static handlers can find the latest resolver
      // even on subsequent reset+execute cycles (Turnstile only takes the callback
      // bindings registered at render time).
      turnstileCallbacksRef.current = { resolve, reject };

      if (turnstileWidgetId.current == null) {
        turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          size: 'invisible',
          callback: (token) => {
            const cb = turnstileCallbacksRef.current.resolve;
            if (cb) { turnstileCallbacksRef.current = { resolve: null, reject: null }; cb(token); }
          },
          'error-callback': () => {
            const cb = turnstileCallbacksRef.current.reject;
            if (cb) { turnstileCallbacksRef.current = { resolve: null, reject: null }; cb(new Error('captcha_error')); }
          },
          'expired-callback': () => {
            const cb = turnstileCallbacksRef.current.reject;
            if (cb) { turnstileCallbacksRef.current = { resolve: null, reject: null }; cb(new Error('captcha_expired')); }
          },
        });
        window.turnstile.execute(turnstileWidgetId.current);
      } else {
        window.turnstile.reset(turnstileWidgetId.current);
        window.turnstile.execute(turnstileWidgetId.current);
      }
    });
  }, []);

  const handleFocus = () => {
    trackEvent('preview_widget_focus');
  };

  const runExplore = useCallback(async (fromLoc, toLoc) => {
    setExplore({
      open: true,
      loading: true,
      error: null,
      suggestions: [],
      fromName: fromLoc.name,
      toName: toLoc.name,
      fromLoc,
      toLoc,
    });
    trackEvent('preview_explore_opened', { from: fromLoc.name, to: toLoc.name });
    try {
      const token = await ensureTurnstile();
      const data = await fetchPublicExplore(
        { lat: fromLoc.lat, lng: fromLoc.lng },
        { lat: toLoc.lat, lng: toLoc.lng },
        token,
      );
      setExplore((s) => ({ ...s, loading: false, suggestions: data.suggestions || [] }));
    } catch (err) {
      const status = err?.status;
      let msg = 'Failed to find suggestions. Please try again.';
      if (status === 429) msg = 'Too many requests — try again in a minute.';
      else if (status === 403) msg = 'Verification failed. Please try again.';
      else if (status === 503) msg = 'The discovery service is busy. Please try again shortly.';
      setExplore((s) => ({ ...s, loading: false, error: msg }));
      trackEvent('preview_explore_error', { status: status || 'unknown' });
    }
  }, [ensureTurnstile]);

  const closeExplore = useCallback(() => {
    setExplore((s) => ({ ...s, open: false }));
  }, []);

  const retryExplore = useCallback(() => {
    if (explore.fromLoc && explore.toLoc) {
      runExplore(explore.fromLoc, explore.toLoc);
    }
  }, [explore.fromLoc, explore.toLoc, runExplore]);

  const runPreview = async (rawDestination, source = 'form') => {
    const dest = (rawDestination || '').trim();
    if (dest.length < 2) {
      setError('Please enter a destination');
      return;
    }
    setError('');
    setLoading(true);
    setPreview(null);
    setShowAttractions(false);
    const t0 = performance.now();
    trackEvent('preview_submitted', { destination: dest, source });
    try {
      const token = await ensureTurnstile();
      const data = await getPublicPreview(dest, token, 'en');
      setPreview(data);
      lastPreview = { destination: dest, preview: data };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ destination: dest, preview: data }));
      } catch {
        // localStorage full or disabled — non-fatal
      }
      trackEvent('preview_rendered', {
        destination: dest,
        cached: !!data.cached,
        latency_ms: Math.round(performance.now() - t0),
      });
      if (onPreviewReady) onPreviewReady(data);
    } catch (err) {
      const status = err?.response?.status;
      let code = 'error';
      if (status === 429) code = 'rate_limited';
      else if (status === 403) code = 'captcha_failed';
      else if (status === 503) code = 'unavailable';

      const userMessage =
        code === 'rate_limited' ? 'Too many requests — try again in a minute.' :
        code === 'captcha_failed' ? 'Verification failed. Please try again.' :
        code === 'unavailable' ? 'Preview service is busy. Please try again shortly.' :
        'Could not generate a preview. Try a different destination.';

      setError(userMessage);
      trackEvent('preview_error', { destination: dest, code });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runPreview(destination);
  };

  // Generate straight away for a destination carried in from another surface —
  // the visitor already asked for this trip on the article they came from.
  useEffect(() => {
    if (!autoRun.current || !destination) return;
    autoRun.current = false;
    runPreview(destination, 'carried');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination]);

  return (
    <div className="w-full max-w-2xl mx-auto mb-6 animate-slide-up" style={{ animationDelay: '0.15s' }}>
      <form
        onSubmit={handleSubmit}
        className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-elevated p-3 flex flex-col sm:flex-row gap-3 border border-primary-100"
      >
        <div className="flex items-center gap-3 flex-1 px-4">
          <FaMapMarkerAlt className="text-primary-500 flex-shrink-0" />
          <input
            type="text"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            onFocus={handleFocus}
            placeholder="Where do you want to go? e.g. Lisbon, Portugal"
            className="flex-1 bg-transparent border-0 focus:outline-none text-lg py-3 min-w-0"
            maxLength={100}
            disabled={loading}
            aria-label="Destination"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 via-accent-500 to-adventure-500 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-glow-primary disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
        >
          {loading ? <FaSpinner className="animate-spin" /> : <FaMagic />}
          {loading ? 'Generating…' : 'Generate Preview'}
        </button>
      </form>

      <div ref={turnstileContainerRef} className="mt-2" />

      {error && (
        <p className="text-coral-600 text-sm mt-2 text-center" role="alert">{error}</p>
      )}

      {renderResults && preview && (
        <div className="mt-6 animate-slide-up text-left">
          <div className="mb-4">
            <h3 className="text-2xl font-bold font-display text-neutral-800">{preview.destination}</h3>
            {preview.tagline && (
              <p className="text-neutral-500 italic mt-1">{preview.tagline}</p>
            )}
          </div>

          <div className="max-w-sm mb-6">
            <PreviewDayCard
              destination={preview.destination}
              attractions={preview.attractions || []}
              weather={preview.weather || null}
              onViewDay={() => {
                setShowAttractions(true);
                trackEvent('preview_view_day_clicked', { destination: preview.destination });
                setTimeout(() => {
                  attractionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 50);
              }}
            />
          </div>

          {/* "Where should I stay?" is the question that follows "what should I
              do?", and answering it here turns a demo into a planning session.
              The date pill is also the trip-intent capture: whatever the visitor
              picks is replayed into their itinerary if they register. */}
          <BookingBlock
            destination={preview.destination}
            surface={`preview_${slugify(preview.destination)}`}
            heading={`Stay near today's stops in ${preview.destination}`}
          />

          {showAttractions && Array.isArray(preview.attractions) && preview.attractions.length > 0 && (
            <div ref={attractionsRef}>
              {preview.attractions.map((a, i) => (
                <PreviewAttractionCard
                  key={`${a.name}-${i}`}
                  attraction={a}
                  destination={preview.destination}
                  delay={i * 600}
                  nextLoc={preview.attractions[i + 1] || null}
                  onExplore={runExplore}
                />
              ))}

              {/* "What will this cost?" is the question a visitor asks once
                  they can see the plan is real. The estimate only runs when
                  they ask for it. */}
              <TripCostEstimator
                initialDestination={preview.destination}
                compact
                surface={`preview_${slugify(preview.destination)}_cost`}
              />

              {/* Second mount: by here the visitor has scrolled a real plan and
                  is several minutes invested — the highest-intent position on
                  the anonymous site. */}
              <BookingBlock
                destination={preview.destination}
                surface={`preview_${slugify(preview.destination)}_end`}
                heading={`Ready to book ${preview.destination}?`}
              />

              {/* The ask, at the only moment it is easy to say yes to: they can
                  see a real plan and the only thing standing between them and
                  keeping it is one field. */}
              <SavePlanByEmail
                destination={preview.destination}
                surface={`preview_${slugify(preview.destination)}`}
              />
            </div>
          )}
        </div>
      )}

      <PreviewExploreModal
        isOpen={renderResults && explore.open}
        onClose={closeExplore}
        fromName={explore.fromName}
        toName={explore.toName}
        loading={explore.loading}
        error={explore.error}
        suggestions={explore.suggestions}
        onRetry={retryExplore}
      />
    </div>
  );
};

export default TryItNowWidget;
